import { DateTime } from 'luxon';
import type { SagaIterator } from 'redux-saga';
import { delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import { getLogger } from '../../utils/core';
import { authSelectors } from '../auth';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { featureFlagsSelectors } from '../feature-flags';
import { rootUiActions, rootUiTypes } from '../root-ui';
import type { NPSSurveyResult } from './npsActions';
import * as actions from './npsActions';

export const DIALOG_TYPE_NPS_SURVEY = 'DIALOG_TYPE_NPS_SURVEY';

const log = getLogger('flux/nps npsSagas');

let surveyTriggeredAt = DateTime.now().minus({ days: 1 });

const isRunningInCypress = (): boolean => {
  const runtimeWindow = (globalThis as typeof globalThis & { window?: unknown }).window;
  return (
    typeof runtimeWindow === 'object' &&
    runtimeWindow !== null &&
    'Cypress' in runtimeWindow &&
    Boolean(runtimeWindow.Cypress)
  );
};

function* checkIfNPSSurveyRequested(): SagaIterator {
  const featureFlags = (yield select(featureFlagsSelectors.getFeatureFlags)) as ReturnType<
    typeof featureFlagsSelectors.getFeatureFlags
  >;
  const npsDelay = (featureFlags.get('nps') as number | undefined) || 10000;
  if (npsDelay > 0 && !isRunningInCypress()) {
    log.log('checkIfNPSSurveyRequestedAction...');
    const authSession = (yield select(authSelectors.getAuthSession)) as ReturnType<typeof authSelectors.getAuthSession>;
    if (authSession.surveyRequested) {
      const daysPassed = DateTime.now().diff(surveyTriggeredAt, 'days').days;
      if (daysPassed >= 1) {
        surveyTriggeredAt = DateTime.now();
        log.log(`Show NPS Survey...in ${npsDelay} msec`);
        yield delay(npsDelay);

        log.log('Show NPS Survey...');
        const dialogData = rootUiTypes.mkRootUiData({
          id: 'NPS',
          type: DIALOG_TYPE_NPS_SURVEY,
          allowNesting: true,
        });
        yield put(rootUiActions.createDialog(dialogData));
      }
    }
  }
}

function* checkIfNPSSurveyRequestedActionWatcher(): SagaIterator {
  yield takeLatest(actions.checkIfNPSSurveyRequestedAction.type, checkIfNPSSurveyRequested);
}

const mkNPSSurveyResourceConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<NPSSurveyResult>();

export function* postNPSSurveyResultActionWatcher(): SagaIterator {
  yield takeEvery(
    actions.postNPSSurveyResultAction.type,
    resourceSagaUtils.qcsSyncCreateResource<NPSSurveyResult>,
    mkNPSSurveyResourceConfig({
      resourceName: 'nps survey',
      resourceBaseUrl: '/users/survey',
      successAction: actions.postNPSSurveyResultSuccessAction,
      failureAction: actions.postNPSSurveyResultFailureAction,
      // Survey submission is fire-and-forget telemetry; a failed POST (e.g. when the user simply
      // dismisses the dialog) should not surface a user-facing error toast.
      ignoreError: true,
    }),
  );
}

export default [checkIfNPSSurveyRequestedActionWatcher, postNPSSurveyResultActionWatcher];
